import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { format, formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Check, CheckCheck, Loader2, MessageSquare, PictureInPicture2, Plus, Send } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/shared/integrations/supabase/client";
import { useAuth } from "@/shared/hooks/useAuth";
import HelpTooltip from "@/shared/components/HelpTooltip";
import {
  useChatSalas,
  useChatMensagens,
  useChatMutations,
  useChatLeiturasSala,
  participantesLeituraDaMensagem,
  CHAT_SALAS_KEY,
  type ChatMensagem,
  type ChatSala,
} from "@/shared/hooks/useChatOperacional";
import { Button } from "@/shared/components/ui/button";
import { Badge } from "@/shared/components/ui/badge";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/shared/components/ui/sheet";
import { ScrollArea } from "@/shared/components/ui/scroll-area";
import { Textarea } from "@/shared/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/components/ui/alert-dialog";
import { cn } from "@/shared/lib/utils";
import ChatNovaSalaModal from "./ChatNovaSalaModal";
import ChatSalaItem from "./ChatSalaItem";

interface ChatOperacionalPanelProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  /** Quando true: renderiza como página fullscreen sem Sheet wrapper.
   *  O botão trigger do ícone fica oculto.
   *  DashboardLayout continua sem passar esta prop (Sheet normal). */
  modoPagina?: boolean;
}

function iniciais(nome: string | undefined) {
  if (!nome?.trim()) return "?";
  const p = nome.trim().split(/\s+/);
  if (p.length === 1) return p[0].slice(0, 2).toUpperCase();
  return (p[0][0] + p[p.length - 1][0]).toUpperCase();
}

/** Gradiente determinístico por nome — mantém a paleta do escritório (bordeaux/ouro/prata). */
const AVATAR_GRADIENTES = [
  "from-[#791614] to-[#5a0f0e]",
  "from-[#9a4a1e] to-[#791614]",
  "from-[#5c4a22] to-[#8a6a2e]",
  "from-[#5a0f0e] to-[#791614]",
  "from-[#6b4f1d] to-[#a17d34]",
  "from-[#7a2a1a] to-[#5a0f0e]",
] as const;

function avatarGradiente(nome: string | undefined) {
  const base = nome?.trim() || "?";
  let hash = 0;
  for (let i = 0; i < base.length; i++) hash = (hash * 31 + base.charCodeAt(i)) >>> 0;
  return AVATAR_GRADIENTES[hash % AVATAR_GRADIENTES.length];
}

function tempoRelativo(iso: string | null | undefined) {
  if (!iso) return "";
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true, locale: ptBR });
  } catch {
    return "";
  }
}

export default function ChatOperacionalPanel({ open, onOpenChange, modoPagina = false }: ChatOperacionalPanelProps) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data: salas = [], isLoading: salasLoading, refetch: refetchSalas } = useChatSalas({
    refetchIntervalMs: open || modoPagina ? 8000 : false,
  });
  const [salaId, setSalaId] = useState<string | null>(null);
  const [novaSalaOpen, setNovaSalaOpen] = useState(false);
  const [texto, setTexto] = useState("");
  const [confirmEncerrar, setConfirmEncerrar] = useState(false);
  const [confirmDeletar, setConfirmDeletar] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const { data: myProfileId } = useQuery({
    queryKey: ["chat-me-profile", user?.id],
    enabled: !!user && (open || modoPagina),
    queryFn: async () => {
      const { data } = await supabase.from("profiles").select("id").eq("user_id", user!.id).maybeSingle();
      return data?.id ?? null;
    },
  });

  const { data: mensagens = [], isLoading: msgLoading } = useChatMensagens(salaId);
  const { data: leiturasSala = [] } = useChatLeiturasSala(salaId);
  const { enviarMensagem, encerrarSala, deletarSala, marcarLido } = useChatMutations();
  const marcarLidoMutate = marcarLido.mutate;

  const salaAtual = useMemo(() => salas.find((s) => s.id === salaId) ?? null, [salas, salaId]);

  const totalNaoLidas = useMemo(() => salas.reduce((acc, s) => acc + (s.nao_lidas ?? 0), 0), [salas]);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      onOpenChange(next);
      if (next) {
        void refetchSalas();
      } else {
        window.setTimeout(() => {
          void qc.invalidateQueries({ queryKey: [CHAT_SALAS_KEY, user?.id] });
        }, 500);
      }
    },
    [onOpenChange, refetchSalas, qc, user?.id],
  );

  useEffect(() => {
    if (open || modoPagina) void refetchSalas();
  }, [open, modoPagina, refetchSalas]);

  const salasOrdenadas = useMemo(() => {
    const abertas = salas.filter((s) => s.status === "aberta");
    const encerradas = salas.filter((s) => s.status === "encerrada");
    return [...abertas, ...encerradas];
  }, [salas]);

  useEffect(() => {
    if (!open && !modoPagina) return;
    if (salaId) return;
    if (salasOrdenadas[0]) setSalaId(salasOrdenadas[0].id);
  }, [open, modoPagina, salaId, salasOrdenadas]);

  useEffect(() => {
    if (!open && !modoPagina) return;
    if (!salaId || !mensagens.length) return;
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [salaId, mensagens.length, open, modoPagina]);

  const ultimaMensagem = mensagens[mensagens.length - 1];
  const ultimaMensagemId = ultimaMensagem?.id ?? "";
  const ultimaMensagemAt = ultimaMensagem?.created_at ?? null;

  const marcarSalaComoLida = useCallback(
    (id: string, ate?: string | null) => {
      marcarLidoMutate({ salaId: id, ate });
    },
    [marcarLidoMutate],
  );

  /** Atualiza marcador de leitura enquanto a sala está aberta (inclui novas mensagens recebidas). */
  useEffect(() => {
    if (!salaId || (!open && !modoPagina)) return;
    const t = window.setTimeout(() => {
      marcarSalaComoLida(salaId, ultimaMensagemAt);
    }, 400);
    return () => window.clearTimeout(t);
  }, [salaId, ultimaMensagemId, ultimaMensagemAt, open, modoPagina, marcarSalaComoLida]);

  const selecionarSala = useCallback(
    (id: string) => {
      setSalaId(id);
      const sala = salas.find((s) => s.id === id);
      marcarSalaComoLida(id, sala?.ultima_mensagem_at ?? null);
    },
    [marcarSalaComoLida, salas],
  );

  const gruposDia = useMemo(() => {
    const map = new Map<string, ChatMensagem[]>();
    for (const m of mensagens) {
      const day = m.created_at.slice(0, 10);
      const list = map.get(day) ?? [];
      list.push(m);
      map.set(day, list);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [mensagens]);

  const handleSend = async () => {
    if (!salaId || !texto.trim() || salaAtual?.status === "encerrada") return;
    const t = texto.trim();
    setTexto("");
    try {
      await enviarMensagem.mutateAsync({ salaId, conteudo: t });
    } catch {
      setTexto(t);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  };

  const participantesTopo = (sala: ChatSala | null) => {
    const parts = sala?.participantes ?? [];
    return parts.slice(0, 4);
  };

  const renderSalasEMensagens = () => (
    <>
      <div className="w-[210px] sm:w-[228px] shrink-0 border-r border-[#a9abae]/25 flex flex-col min-h-0 bg-[#f4efe6]/60">
        <div className="flex items-center justify-between px-3 pt-3 pb-2">
          <span className="font-accent text-[9px] uppercase tracking-[0.2em] text-muted-foreground">
            Salas
          </span>
          {salasOrdenadas.length > 0 && (
            <span className="font-accent text-[9px] uppercase tracking-wider text-[#791614]/70">
              {salasOrdenadas.length}
            </span>
          )}
        </div>
        <ScrollArea className="flex-1 px-2 pb-2">
          {salasLoading ? (
            <div className="flex justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-[#791614]" />
            </div>
          ) : salasOrdenadas.length === 0 ? (
            <div className="flex flex-col items-center gap-2 px-3 py-8 text-center">
              <MessageSquare className="h-7 w-7 text-[#a9abae]/60" strokeWidth={1.5} aria-hidden />
              <p className="text-[11px] text-muted-foreground font-body leading-snug">
                Nenhuma sala ainda.
                <br />
                Crie a primeira em “Nova sala”.
              </p>
            </div>
          ) : (
            <div className="space-y-1.5">
              {salasOrdenadas.map((s) => (
                <ChatSalaItem
                  key={s.id}
                  sala={s}
                  selected={s.id === salaId}
                  tempoRelativo={tempoRelativo(s.ultima_mensagem_at ?? s.updated_at)}
                  onSelect={() => selecionarSala(s.id)}
                />
              ))}
            </div>
          )}
        </ScrollArea>
      </div>

      <div className="flex-1 flex flex-col min-w-0 min-h-0 bg-[#f9f6f0]">
        {!salaAtual ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-3 text-center px-6">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#791614]/8 border border-[#791614]/15">
              <MessageSquare className="h-6 w-6 text-[#791614]/70" strokeWidth={1.5} aria-hidden />
            </div>
            <p className="text-sm text-muted-foreground font-body max-w-[220px]">
              Selecione uma sala à esquerda ou crie uma nova conversa.
            </p>
          </div>
        ) : (
          <>
            <div className="shrink-0 px-4 py-2.5 border-b border-[#a9abae]/25 bg-[rgba(249,246,240,0.85)] backdrop-blur-sm">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="font-display text-[17px] leading-tight text-[#791614] truncate">{salaAtual.titulo}</h3>
                  <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                    <div className="flex -space-x-2">
                      {participantesTopo(salaAtual).map((p) => (
                        <span
                          key={p.id}
                          className={cn(
                            "inline-flex h-7 w-7 items-center justify-center rounded-full ring-2 ring-[#f9f6f0] text-[10px] font-semibold text-[#f9f6f0] bg-gradient-to-br",
                            avatarGradiente(p.nome),
                          )}
                          title={p.nome}
                        >
                          {iniciais(p.nome)}
                        </span>
                      ))}
                    </div>
                    {(salaAtual.participantes?.length ?? 0) > 4 && (
                      <span className="text-[10px] text-muted-foreground font-body">
                        +{(salaAtual.participantes?.length ?? 0) - 4}
                      </span>
                    )}
                    <HelpTooltip text="Nas mensagens que você envia, aparece quem já tinha esta sala aberta depois daquele horário (marcador de leitura). Quem ainda não abriu desde então aparece como pendente." />
                  </div>
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  {salaAtual.status === "aberta" && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs border-[#a9abae]/40"
                      onClick={() => setConfirmEncerrar(true)}
                    >
                      Encerrar
                    </Button>
                  )}
                  {myProfileId && salaAtual.created_by === myProfileId && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs text-destructive hover:text-destructive"
                      onClick={() => setConfirmDeletar(true)}
                    >
                      Deletar
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {salaAtual.status === "encerrada" && (
              <div className="px-3 py-2 space-y-2 bg-[#791614]/10 border-b border-[#a9abae]/30 text-xs text-[#791614] font-body">
                <p className="text-center">Sala encerrada — não é possível enviar novas mensagens.</p>
                {salaAtual.ata_texto?.trim() && (
                  <div className="rounded-md border border-[#a9abae]/30 bg-[#f9f6f0] p-2 text-left text-foreground">
                    <p className="font-accent text-[9px] uppercase tracking-widest text-muted-foreground mb-1">
                      Ata da conversa (IA)
                    </p>
                    <div className="text-xs font-body whitespace-pre-wrap max-h-40 overflow-y-auto text-foreground">
                      {salaAtual.ata_texto}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div
              ref={scrollRef}
              className="flex-1 overflow-y-auto px-4 py-3 space-y-4 min-h-0 bg-[linear-gradient(180deg,rgba(215,179,105,0.04),rgba(249,246,240,0)_120px)]"
            >
              {msgLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-[#791614]" />
                </div>
              ) : mensagens.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-2 py-12 text-center">
                  <MessageSquare className="h-8 w-8 text-[#a9abae]/50" strokeWidth={1.5} aria-hidden />
                  <p className="text-xs text-muted-foreground font-body">
                    Nenhuma mensagem ainda. Envie a primeira abaixo.
                  </p>
                </div>
              ) : (
                gruposDia.map(([dia, msgs]) => (
                  <div key={dia}>
                    <div className="flex items-center justify-center mb-3">
                      <span className="font-accent text-[9px] uppercase tracking-widest text-[#5c4a22] bg-[#d7b369]/15 border border-[#d7b369]/30 rounded-full px-3 py-0.5">
                        {format(new Date(dia + "T12:00:00"), "EEEE, d MMM yyyy", { locale: ptBR })}
                      </span>
                    </div>
                    <div className="space-y-2.5">
                      {msgs.map((m) => {
                        const minha = myProfileId && m.autor_id === myProfileId;
                        const { lidos, pendentes } = participantesLeituraDaMensagem(
                          m.created_at,
                          leiturasSala,
                          salaAtual.participantes ?? [],
                          m.autor_id,
                        );
                        const temOutros = lidos.length + pendentes.length > 0;
                        const tituloLeitura =
                          lidos.length && !pendentes.length
                            ? `Todos visualizaram: ${lidos.map((p) => p.nome).join(", ")}`
                            : lidos.length
                              ? `Visualizaram: ${lidos.map((p) => p.nome).join(", ")}. Pendente: ${pendentes.map((p) => p.nome).join(", ")}`
                              : pendentes.length
                                ? `Ainda não visualizado por: ${pendentes.map((p) => p.nome).join(", ")}`
                                : "";
                        const textoLeituraCurto =
                          lidos.length && !pendentes.length
                            ? "Visto por todos"
                            : lidos.length
                              ? `Visto: ${lidos.map((p) => p.nome).join(", ")}${
                                  pendentes.length ? ` · ${pendentes.length} pendente(s)` : ""
                                }`
                              : pendentes.length
                                ? "Ninguém visualizou ainda"
                                : "";
                        const todosLeram = lidos.length > 0 && pendentes.length === 0;
                        return (
                          <div
                            key={m.id}
                            className={cn("flex gap-2 items-end", minha ? "justify-end" : "justify-start")}
                          >
                            {!minha && (
                              <span
                                className={cn(
                                  "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full ring-2 ring-[#f9f6f0] text-[11px] font-semibold text-[#f9f6f0] bg-gradient-to-br",
                                  avatarGradiente(m.autor_nome),
                                )}
                              >
                                {iniciais(m.autor_nome)}
                              </span>
                            )}
                            <div
                              className={cn(
                                "max-w-[78%] px-3.5 py-2 text-sm font-body shadow-sm",
                                minha
                                  ? "rounded-2xl rounded-br-md bg-gradient-to-br from-[#791614] to-[#5a0f0e] text-[#f9f6f0]"
                                  : "rounded-2xl rounded-bl-md bg-white text-foreground border border-[#a9abae]/25",
                              )}
                            >
                              {!minha && (
                                <p className="text-[10px] font-accent uppercase tracking-wide text-[#791614]/80 mb-0.5">
                                  {m.autor_nome}
                                </p>
                              )}
                              <p className="whitespace-pre-wrap break-words leading-relaxed">{m.conteudo}</p>
                              <div
                                className={cn(
                                  "flex items-center gap-1 mt-1 justify-end",
                                  minha ? "text-[#f9f6f0]/70" : "text-muted-foreground",
                                )}
                              >
                                <span className="text-[10px]">
                                  {format(new Date(m.created_at), "HH:mm", { locale: ptBR })}
                                </span>
                                {minha && temOutros && (
                                  <span title={tituloLeitura} className="inline-flex items-center">
                                    {todosLeram ? (
                                      <CheckCheck className="h-3.5 w-3.5 text-[#d7b369]" aria-hidden />
                                    ) : (
                                      <Check className="h-3.5 w-3.5 text-[#f9f6f0]/60" aria-hidden />
                                    )}
                                  </span>
                                )}
                              </div>
                              {minha && temOutros && textoLeituraCurto && (
                                <p
                                  className="text-[10px] text-[#f9f6f0]/60 text-right leading-tight mt-0.5 max-w-[min(260px,72vw)]"
                                  title={tituloLeitura}
                                >
                                  {textoLeituraCurto}
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="shrink-0 border-t border-[#a9abae]/25 p-3 bg-[rgba(249,246,240,0.9)] backdrop-blur-sm">
              <div className="flex items-end gap-2">
                <Textarea
                  value={texto}
                  onChange={(e) => setTexto(e.target.value)}
                  onKeyDown={onKeyDown}
                  placeholder={
                    salaAtual.status === "encerrada"
                      ? "Sala encerrada"
                      : "Mensagem… (Enter envia, Shift+Enter nova linha)"
                  }
                  disabled={salaAtual.status === "encerrada" || enviarMensagem.isPending}
                  rows={1}
                  className="flex-1 min-w-0 resize-none border-[#a9abae]/30 bg-white text-sm min-h-[44px] max-h-32 rounded-2xl px-4 py-2.5 focus-visible:ring-[#791614]/25"
                />
                <Button
                  type="button"
                  size="icon"
                  className="h-11 w-11 shrink-0 rounded-full bg-[#791614] hover:bg-[#5a0f0e] text-[#f9f6f0] shadow-sm transition-transform active:scale-95 disabled:opacity-40"
                  aria-label="Enviar mensagem"
                  disabled={
                    salaAtual.status === "encerrada" || !texto.trim() || enviarMensagem.isPending
                  }
                  onClick={() => void handleSend()}
                >
                  {enviarMensagem.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" aria-hidden />
                  )}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );

  return (
    <>
      {!modoPagina && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="relative shrink-0 h-10 w-10 overflow-visible border border-transparent hover:border-[#d7b369]/45 hover:bg-[#d7b369]/10"
          title="Chat operacional — clique para abrir ao lado. Botão do meio do mouse: janela separada (900×650)."
          aria-label="Abrir chat operacional entre advogados do escritório"
          onClick={() => handleOpenChange(true)}
          onMouseDown={(e) => {
            if (e.button === 1) {
              e.preventDefault();
              window.open(
                "/chat-standalone",
                "chat-ferrugini",
                "width=900,height=650,resizable=yes,scrollbars=no,toolbar=no,menubar=no,location=no,status=no",
              );
            }
          }}
        >
          <MessageSquare
            className="h-4 w-4 text-[#791614] stroke-[#791614]"
            strokeWidth={2}
            aria-hidden
          />
          {totalNaoLidas > 0 && (
            <span
              className="absolute -top-1 -right-1 z-10 h-4 min-w-[16px] px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center ring-2 ring-[rgba(249,246,240,0.98)] shadow-sm pointer-events-none"
              aria-hidden
            >
              {totalNaoLidas > 99 ? "99+" : totalNaoLidas}
            </span>
          )}
        </Button>
      )}

      {modoPagina ? (
        <div className="flex flex-col h-full bg-[#f9f6f0] overflow-hidden">
          <div className="relative px-4 py-3 border-b border-[#a9abae]/30 space-y-0 shrink-0">
            <div className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-[#d7b369]/0 via-[#d7b369] to-[#d7b369]/0" aria-hidden />
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <h2 className="font-display text-xl text-[#791614] flex items-center gap-2">
                  <MessageSquare className="h-5 w-5 shrink-0" strokeWidth={1.75} />
                  Chat operacional
                </h2>
                <p className="text-[11px] text-muted-foreground font-body mt-0.5">
                  Conversas em tempo real entre a equipe do escritório.
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {totalNaoLidas > 0 && (
                  <Badge variant="secondary" className="font-accent text-[9px] uppercase tracking-wider">
                    {totalNaoLidas} não lidas
                  </Badge>
                )}
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-[#a9abae] hover:text-[#791614] hover:bg-[#d7b369]/10"
                  title="Abrir em janela independente"
                  aria-label="Abrir chat em janela independente"
                  onClick={() => {
                    window.open(
                      "/chat-standalone",
                      "chat-ferrugini",
                      "width=900,height=650,resizable=yes,scrollbars=no,toolbar=no,menubar=no,location=no,status=no",
                    );
                  }}
                >
                  <PictureInPicture2 className="h-4 w-4" aria-hidden />
                </Button>
              </div>
            </div>
            <Button
              size="sm"
              className="mt-2.5 w-fit bg-[#791614] hover:bg-[#5a0f0e] text-[#f9f6f0] gap-1.5 shrink-0 rounded-full"
              onClick={() => setNovaSalaOpen(true)}
            >
              <Plus className="h-3.5 w-3.5 shrink-0" aria-hidden />
              <span className="whitespace-nowrap">Nova sala</span>
            </Button>
          </div>
          <div className="flex flex-1 min-h-0 min-w-0 overflow-hidden">
            {renderSalasEMensagens()}
          </div>
        </div>
      ) : (
        <Sheet open={open} onOpenChange={handleOpenChange}>
          <SheetContent
            side="right"
            className="w-full sm:max-w-[540px] max-w-[100vw] p-0 gap-0 flex flex-col h-[100dvh] bg-[#f9f6f0] border-l border-[#a9abae]/30 overflow-hidden"
          >
            <SheetDescription className="sr-only">
              Conversas em tempo real entre advogados do escritório.
            </SheetDescription>
            <SheetHeader className="relative px-4 py-3 border-b border-[#a9abae]/30 space-y-0 shrink-0 text-left">
              <div className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-[#d7b369]/0 via-[#d7b369] to-[#d7b369]/0" aria-hidden />
              <div className="flex items-center justify-between gap-2 pr-8">
                <div className="min-w-0">
                  <SheetTitle className="font-display text-xl text-[#791614] flex items-center gap-2">
                    <MessageSquare className="h-5 w-5 shrink-0" strokeWidth={1.75} />
                    Chat operacional
                  </SheetTitle>
                  <p className="text-[11px] text-muted-foreground font-body mt-0.5">
                    Conversas em tempo real entre a equipe.
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {totalNaoLidas > 0 && (
                    <Badge variant="secondary" className="font-accent text-[9px] uppercase tracking-wider">
                      {totalNaoLidas} não lidas
                    </Badge>
                  )}
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-[#a9abae] hover:text-[#791614] hover:bg-[#d7b369]/10"
                    title="Abrir em janela independente"
                    aria-label="Abrir chat em janela independente"
                    onClick={() => {
                      window.open(
                        "/chat-standalone",
                        "chat-ferrugini",
                        "width=900,height=650,resizable=yes,scrollbars=no,toolbar=no,menubar=no,location=no,status=no",
                      );
                    }}
                  >
                    <PictureInPicture2 className="h-4 w-4" aria-hidden />
                  </Button>
                </div>
              </div>
              <Button
                size="sm"
                className="mt-2.5 w-fit bg-[#791614] hover:bg-[#5a0f0e] text-[#f9f6f0] gap-1.5 shrink-0 rounded-full"
                onClick={() => setNovaSalaOpen(true)}
              >
                <Plus className="h-3.5 w-3.5 shrink-0" aria-hidden />
                <span className="whitespace-nowrap">Nova sala</span>
              </Button>
            </SheetHeader>

            <div className="flex flex-1 min-h-0">
              {renderSalasEMensagens()}
            </div>
          </SheetContent>
        </Sheet>
      )}

      <ChatNovaSalaModal
        open={novaSalaOpen}
        onOpenChange={setNovaSalaOpen}
        onSalaCriada={(id) => {
          setSalaId(id);
          selecionarSala(id);
        }}
      />

      <AlertDialog open={confirmEncerrar} onOpenChange={setConfirmEncerrar}>
        <AlertDialogContent className="bg-[#f9f6f0] border-[#a9abae]/30">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Encerrar sala?</AlertDialogTitle>
            <AlertDialogDescription className="font-body">
              Ninguém poderá enviar novas mensagens após o encerramento.
              {salaAtual?.ata_destino && salaAtual.ata_destino !== "nenhum" && (
                <span className="block mt-2">
                  Será gerada uma ata resumida com IA
                  {salaAtual.ata_destino === "vinculo" ? " e registrada no vínculo do processo, quando possível." : " e ficará disponível nesta sala."}
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-[#791614] hover:bg-[#5a0f0e] text-[#f9f6f0]"
              onClick={() => {
                if (salaId && salaAtual) {
                  void encerrarSala.mutateAsync({
                    salaId: salaAtual.id,
                    ataDestino: salaAtual.ata_destino,
                  });
                }
                setConfirmEncerrar(false);
              }}
            >
              Encerrar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmDeletar} onOpenChange={setConfirmDeletar}>
        <AlertDialogContent className="bg-[#f9f6f0] border-[#a9abae]/30">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Remover sala?</AlertDialogTitle>
            <AlertDialogDescription className="font-body">
              Esta ação exclui a sala e o histórico para todos os participantes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (salaId) {
                  void deletarSala.mutateAsync(salaId).then(() => {
                    setSalaId(null);
                  });
                }
                setConfirmDeletar(false);
              }}
            >
              Deletar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
